import { useState, useEffect, useRef } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useAppContext } from '@/context/AppContext';
import storage from '@/utils/storage';

interface UseComfyLogsOptions {
  comfyUrl: string;
  enabled: boolean;
}

export const useComfyLogs = ({ comfyUrl, enabled }: UseComfyLogsOptions) => {
  const [logs, setLogs] = useState<string[]>([]);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isFetchingFromSSE, setIsFetchingFromSSE] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const resetIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const access_token = storage.getToken();
  const { xTeamId: xTeamIdFromTeamApi } = useAppContext();
  const teamData = storage?.getTeam();
  const xTeamIdFromLocalStorage = teamData ? teamData._id : null;
  const xTeamId = xTeamIdFromTeamApi || xTeamIdFromLocalStorage;

  const setupEventSource = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = `${comfyUrl}/flowscale/log/stream`;
    const eventSource = new EventSourcePolyfill(
      url
      //    {
      //   headers: {
      //     Authorization: `Bearer ${access_token}`,
      //     'X-Team': xTeamId,
      //   },
      //   withCredentials: false,
      // }
    );
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsReconnecting(false);
      setIsFetchingFromSSE(true);
      // Clear reconnect timer if connection is successful
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    eventSource.onmessage = (event) => {
      setLogs((prevLogs) => [...prevLogs, event.data]);
    };

    eventSource.onerror = () => {
      setIsReconnecting(true);
      setIsFetchingFromSSE(false);
      eventSource.close();

      // Attempt to reconnect after 5 seconds
      reconnectTimerRef.current = setTimeout(() => {
        setupEventSource();
      }, 5000);
    };

    // Reset connection every 90 seconds to prevent stale connections
    if (resetIntervalRef.current) {
      clearInterval(resetIntervalRef.current);
    }
    resetIntervalRef.current = setInterval(() => {
      setupEventSource();
    }, 90000);
  };

  const closeSSEConnection = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (resetIntervalRef.current) {
      clearInterval(resetIntervalRef.current);
      resetIntervalRef.current = null;
    }
    setIsFetchingFromSSE(false);
  };

  useEffect(() => {
    if (enabled && comfyUrl) {
      setupEventSource();
    } else {
      closeSSEConnection();
    }

    return () => {
      closeSSEConnection();
    };
  }, [enabled, comfyUrl]);

  return {
    logs,
    isReconnecting,
    isFetchingFromSSE,
    closeSSEConnection,
  };
};
